import {
  Box,
  Breadcrumbs,
  Card,
  CircularProgress,
  FormLabel,
  Grid,
  Input,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { pageTitle } from '@/utils/pageTitle';
import { useMemo } from 'react';
import { formatDateUTC } from '@/utils/date';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useLedger } from '@/hooks/useLedgers';
import { useSupplier } from '@/hooks/useSuppliers';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/ledgers/buys/$id')({
  component: BuyLedgerDetailPage,
  head: ({ params }) => pageTitle(`Pagos / ${params.id}`),
});

function BuyLedgerDetailPage() {
  const { id } = Route.useParams();
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('BUY_READ');

  const { data: ledger, isLoading } = useLedger(Number(id));
  const { data: supplier } = useSupplier(ledger?.supplierId?.toString() || '', {
    enabled: !!ledger?.supplierId,
  });

  const referenceLink = useMemo(() => {
    if (ledger?.supplierId && ledger?.referenceId) {
      return (
        <Link to='/buys/$id' params={{ id: ledger.referenceId.toString() }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Ir a compra <OpenInNewIcon />
          </Box>
        </Link>
      );
    }

    return null;
  }, [ledger]);

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/ledgers/buys' />;
  }

  if (!ledger) {
    return <div>Pago no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/ledgers/buys'>Pagos</Link>
          <Typography>{ledger.id}</Typography>
        </Breadcrumbs>
      </Stack>

      <Card>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {referenceLink && <Grid xs={12}>{referenceLink}</Grid>}

          <Grid xs={12} md={6}>
            <FormLabel>Fecha</FormLabel>
            <Input
              value={formatDateUTC(ledger.date)}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Cuenta</FormLabel>
            <Input
              value={ledger.accountName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Proveedor</FormLabel>
            <Input
              value={supplier?.name ?? ''}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Monto</FormLabel>
            <Input
              value={ledger.amount.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                style: 'currency',
                currency: 'ARS',
              })}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12}>
            <FormLabel>Descripción</FormLabel>
            <Textarea
              value={ledger.description ?? ''}
              readOnly
              minRows={3}
              maxRows={6}
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}
